import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const CONTACTS_PER_PAGE = 5;

// Helper to parse UTC date string and return Date object using UTC methods
const parseUTCDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

// Helper to format date using UTC methods
const formatDateUTC = (date) => {
  if (!date) return '';
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${day}-${month}`;
};

// Helper to format time using UTC methods
const formatTimeUTC = (date) => {
  if (!date) return '';
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

function CustomerInfoPage({ apiBaseUrl }) {
  const { bedrijfCode = '' } = useParams();
  const navigate = useNavigate();

  const decodedBedrijf = decodeURIComponent(bedrijfCode);

  const [customerData, setCustomerData] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentContactPage, setCurrentContactPage] = useState(1);
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('contacts'); // 'contacts' | 'agenda'
  const [agendaItems, setAgendaItems] = useState([]);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const mapsQuery = useMemo(() => {
    if (!customerData) return '';
    const parts = [];
    if (customerData.Straat) parts.push(customerData.Straat);
    if (customerData.PostCode && customerData.Plaats) {
      parts.push(`${customerData.PostCode} ${customerData.Plaats}`);
    }
    if (customerData.Land && customerData.Land !== 'NL') parts.push(customerData.Land);
    const address = parts.join(', ').trim();
    return address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : '';
  }, [customerData]);

  useEffect(() => {
    const fetchCustomerData = async () => {
      const trimmed = decodedBedrijf.trim();
      if (!trimmed) {
        setError('Geen bedrijf opgegeven');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = await axios.get(`${apiBaseUrl}/klant-info/${encodeURIComponent(trimmed)}`);
        if (response.data?.success) {
          setCustomerData(response.data.data);
          fetchContactpersonen(trimmed);
        } else {
          setError(response.data?.message || 'Geen klant data gevonden');
          setCustomerData(null);
        }
      } catch (err) {
        console.error('Error fetching customer data:', err);
        setError(err.response?.data?.message || 'Fout bij ophalen van klant informatie');
        setCustomerData(null);
      } finally {
        setLoading(false);
      }
    };

    const fetchContactpersonen = async (bedrijf) => {
      if (!bedrijf) return;
      try {
        setContactsLoading(true);
        const response = await axios.get(`${apiBaseUrl}/klant-contactpersonen/${encodeURIComponent(bedrijf)}`);
        if (response.data?.success) {
          setContacts(response.data.data || []);
        } else {
          setContacts([]);
        }
      } catch (err) {
        console.error('Error fetching contactpersonen:', err);
        setContacts([]);
      } finally {
        setContactsLoading(false);
      }
    };

    fetchCustomerData();
  }, [apiBaseUrl, decodedBedrijf]);

  useEffect(() => {
    const fetchAgendaForCustomer = async () => {
      const trimmed = decodedBedrijf.trim();
      if (!trimmed) return;
      try {
        setAgendaLoading(true);
        // Fetch all items and filter client-side for this klant and future dates
        const response = await axios.get(`${apiBaseUrl}/agenda-items`);
        if (response.data?.success) {
          const items = response.data.data || [];
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          const filtered = items.filter((item) => {
            const bedrijf = (item.bedrijf || '').toString().trim();
            if (bedrijf !== trimmed) return false;
            const date = parseUTCDate(item.datum);
            if (!date) return false;
            // Compare UTC dates
            const itemDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
            return itemDate.getTime() >= now.getTime();
          });
          // Sort by start time (using UTC)
          filtered.sort((a, b) => {
            const aDate = parseUTCDate(a.actie_tijd_start || a.datum);
            const bDate = parseUTCDate(b.actie_tijd_start || b.datum);
            if (!aDate || !bDate) return 0;
            return aDate.getTime() - bDate.getTime();
          });
          setAgendaItems(filtered);
        } else {
          setAgendaItems([]);
        }
      } catch (err) {
        console.error('Error fetching agenda items for klant:', err);
        setAgendaItems([]);
      } finally {
        setAgendaLoading(false);
      }
    };
    fetchAgendaForCustomer();
  }, [apiBaseUrl, decodedBedrijf]);

  useEffect(() => {
    setCurrentContactPage(1);
  }, [contacts]);

  useEffect(() => {
    setCurrentContactPage(1);
  }, [contactSearchTerm]);

  const filteredContacts = useMemo(() => {
    if (!contactSearchTerm.trim()) {
      return contacts;
    }
    const searchLower = contactSearchTerm.trim().toLowerCase();
    return contacts.filter((contact) => {
      const naam = (contact.VolledigeNaam || '').toLowerCase();
      const email = (contact.Email || '').toLowerCase();
      const telefoon = (contact.Telefoon || '').toLowerCase();
      const telmobiel = (contact.Telmobiel || '').toLowerCase();
      const functie = (contact.Functie || '').toLowerCase();
      return (
        naam.includes(searchLower) ||
        email.includes(searchLower) ||
        telefoon.includes(searchLower) ||
        telmobiel.includes(searchLower) ||
        functie.includes(searchLower)
      );
    });
  }, [contacts, contactSearchTerm]);

  const totalContactPages = Math.ceil(filteredContacts.length / CONTACTS_PER_PAGE);
  const currentContacts = useMemo(() => {
    if (filteredContacts.length === 0) return [];
    const startIndex = (currentContactPage - 1) * CONTACTS_PER_PAGE;
    return filteredContacts.slice(startIndex, startIndex + CONTACTS_PER_PAGE);
  }, [filteredContacts, currentContactPage]);

  const showContactsSection = contacts.length > 0;

  return (
    <div className="customer-info-page">
      <header className="customer-info-page__header">
        <div className="customer-info-page__nav">
          <button
            type="button"
            className="customer-info-page__nav-button"
            onClick={() => navigate(-1)}
          >
            ← Terug
          </button>
          <button
            type="button"
            className="customer-info-page__nav-button secondary"
            onClick={() => navigate('/')}
          >
            Naar dashboard
          </button>
        </div>
        <div className="customer-info-page__title">
          <span>Klant informatie</span>
          <h1>{customerData?.Naam || decodedBedrijf || 'Onbekende klant'}</h1>
          {customerData?.Klantcode && (
            <p className="customer-info-page__subtitle">Klantcode: {customerData.Klantcode}</p>
          )}
        </div>
      </header>

      <main className="customer-info-page__body">
        <div className="customer-info-page__panel klant-info-content">
          {loading ? <div className="loading">Laden...</div> : error ? <div className="error-message">{error}</div> : (
            <>
              <section className="customer-info-page__top">
                {customerData ? (
                  <div className="customer-top-grid">
                    <div className="customer-top-left">
                      <dl className="agenda-detail-list">
                        {(customerData.Straat || customerData.PostCode || customerData.Plaats) && (
                          <div className="agenda-detail-row">
                            <dt>Adres</dt>
                            <dd>
                              {customerData.Straat && <div>{customerData.Straat}</div>}
                              {customerData.PostCode && customerData.Plaats && (
                                <div>{customerData.PostCode} {customerData.Plaats}</div>
                              )}
                              {customerData.Land && customerData.Land !== 'NL' && (
                                <div>{customerData.Land}</div>
                              )}
                              {mapsQuery && (
                                <div className="customer-map-link-wrap">
                                  <a
                                    className="customer-map-link"
                                    href={mapsQuery}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Openen in Google Maps"
                                  >
                                    Open in Maps
                                  </a>
                                </div>
                              )}
                            </dd>
                          </div>
                        )}
                        {customerData.www && (
                          <div className="agenda-detail-row">
                            <dt>Website</dt>
                            <dd>
                              <a
                                href={customerData.www.startsWith('http') ? customerData.www : `https://${customerData.www}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="contact-link"
                              >
                                {customerData.www}
                              </a>
                            </dd>
                          </div>
                        )}
                        {customerData.HeeftSupportJN && (
                          <div className="agenda-detail-row">
                            <dt>Support</dt>
                            <dd>{customerData.HeeftSupportJN === 'J' ? 'Ja' : 'Nee'}</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                    <div className="customer-top-right">
                      <div className="customer-actions">
                        {customerData.Telefoon && (
                          <a href={`tel:${customerData.Telefoon.replace(/\s/g, '')}`} className="customer-action-button">
                            Bel: {customerData.Telefoon}
                          </a>
                        )}
                        {customerData.Email && (
                          <a href={`mailto:${customerData.Email.trim()}`} className="customer-action-button">
                            Mail: {customerData.Email.trim()}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="empty-message">Geen klant informatie beschikbaar</div>
                )}
              </section>

              <section className="customer-info-page__bottom">
                <div className="customer-tabs">
                  <button
                    type="button"
                    className={`customer-tab ${activeTab === 'contacts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('contacts')}
                  >
                    Contactpersonen
                  </button>
                  <button
                    type="button"
                    className={`customer-tab ${activeTab === 'agenda' ? 'active' : ''}`}
                    onClick={() => setActiveTab('agenda')}
                  >
                    Afspraken
                  </button>
                </div>
                <div className="customer-tabpanes">
                  {activeTab === 'contacts' ? (
                    <div className="customer-tabpane">
                      {showContactsSection && (
                        <div className="agenda-detail-contacts-section klant-info-contacts">
                          <div className="contacts-section-header">
                            <h3 className="contacts-section-title">
                              Contactpersonen ({filteredContacts.length}{contactSearchTerm ? ` van ${contacts.length}` : ''})
                            </h3>
                            <input
                              type="text"
                              className="contacts-search-input"
                              placeholder="Zoek contactpersoon..."
                              value={contactSearchTerm}
                              onChange={(event) => setContactSearchTerm(event.target.value)}
                            />
                          </div>
                          {contactsLoading ? (
                            <div className="loading">Contactpersonen laden...</div>
                          ) : filteredContacts.length === 0 ? (
                            <div className="empty-message">Geen contactpersonen gevonden</div>
                          ) : (
                            <>
                              <div className="contacts-list-compact">
                                {currentContacts.map((contact, index) => (
                                  <div key={`${contact.VolledigeNaam || index}-${index}`} className="contact-item-compact">
                                    <div className="contact-name-compact">
                                      {contact.VolledigeNaam || '-'}
                                      {contact.Functie && (
                                        <span className="contact-functie"> - {contact.Functie}</span>
                                      )}
                                    </div>
                                    <div className="contact-details-compact">
                                      {contact.Email && (
                                        <a
                                          href={`mailto:${contact.Email.trim()}`}
                                          className="contact-link-compact"
                                          title={contact.Email.trim()}
                                        >
                                          {contact.Email.trim()}
                                        </a>
                                      )}
                                      {contact.Telefoon && (
                                        <a
                                          href={`tel:${contact.Telefoon.replace(/\s/g, '')}`}
                                          className="contact-link-compact"
                                          title={contact.Telefoon}
                                        >
                                          {contact.Telefoon}
                                        </a>
                                      )}
                                      {contact.Telmobiel && (
                                        <a
                                          href={`tel:${contact.Telmobiel.replace(/\s/g, '')}`}
                                          className="contact-link-compact"
                                          title={contact.Telmobiel}
                                        >
                                          Mobiel: {contact.Telmobiel}
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {totalContactPages > 1 && (
                                <div className="contacts-pagination">
                                  <button
                                    type="button"
                                    className="contacts-pagination-button"
                                    onClick={() => setCurrentContactPage((prev) => Math.max(1, prev - 1))}
                                    disabled={currentContactPage === 1}
                                    aria-label="Vorige pagina"
                                  >
                                    Vorige
                                  </button>
                                  <span className="contacts-pagination-info">
                                    Pagina {currentContactPage} van {totalContactPages}
                                  </span>
                                  <button
                                    type="button"
                                    className="contacts-pagination-button"
                                    onClick={() =>
                                      setCurrentContactPage((prev) => Math.min(totalContactPages, prev + 1))
                                    }
                                    disabled={currentContactPage === totalContactPages}
                                    aria-label="Volgende pagina"
                                  >
                                    Volgende
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      {!showContactsSection && <div className="empty-message">Geen contactpersonen</div>}
                    </div>
                  ) : (
                    <div className="customer-tabpane">
                      {agendaLoading ? (
                        <div className="loading">Afspraken laden...</div>
                      ) : agendaItems.length === 0 ? (
                        <div className="empty-message">Geen aankomende afspraken</div>
                      ) : (
                        <div className="customer-agenda-list">
                          {agendaItems.map((item) => {
                            const datumDate = parseUTCDate(item.datum);
                            const startDate = parseUTCDate(item.actie_tijd_start);
                            const stopDate = item.actie_tijd_stop ? parseUTCDate(item.actie_tijd_stop) : null;
                            
                            return (
                              <div key={item.id} className="customer-agenda-item">
                                <div className="customer-agenda-item__header">
                                  <span className="compact-date">
                                    {formatDateUTC(datumDate || startDate)}
                                  </span>
                                  <span className="compact-time">
                                    {startDate ? (
                                      stopDate 
                                        ? `${formatTimeUTC(startDate)} - ${formatTimeUTC(stopDate)}`
                                        : formatTimeUTC(startDate)
                                    ) : '-'}
                                  </span>
                                  {item.locatie && (
                                    <span className="compact-location-code" title={`Locatie ${item.locatie}`}>
                                      {item.locatie}
                                    </span>
                                  )}
                                </div>
                                <div className="customer-agenda-item__body">
                                  <div className="compact-omschrijving">{item.actie_omschrijving || '-'}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default CustomerInfoPage;

